import styled, { css } from 'styled-components'

import { mq, sizes } from '../../theme'
import ActionButton from '../ActionButton'

export const Item = styled.div<{ $isDisabled?: boolean }>`
  align-items: center;
  justify-content: space-between;
  padding-right: 1.5rem;
  position: relative;
  border-top-right-radius: 1rem;
  border-bottom-right-radius: 1rem;
  padding-left: 2.2rem;
  background-color: ${({ theme }) => theme.colors.brownBack};

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      opacity: 0.3;
    `}

  .columnAction {
    background-color: ${({ theme }) => theme.colors.brownBack};
    border-top-right-radius: 1rem;
    border-bottom-right-radius: 1rem;
  }

  @media ${mq(null, sizes.tablet)} {
    flex-direction: column;
    height: auto;
    padding: 2rem;
  }

  ${({ theme }) => theme.breakpoints.up('tablet')} {
    display: table-row;
    width: 100%;
    clear: both;
  }
`

export const Strip = styled.div<{ $isActive?: boolean }>`
  height: 100%;
  width: 1rem;
  background-color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.pinkPrimary : theme.colors.greyFields};
  position: absolute;
  left: 0;
  top: 0;
`

const SeparatorStyleCommon = css`
  position: absolute;
  top: 0;
  right: 0;
  background-image: repeating-linear-gradient(
      0deg,
      ${({ theme }) => theme.colors.greyContour},
      ${({ theme }) => theme.colors.greyContour} 1rem,
      transparent 1rem,
      transparent 1.6rem,
      ${({ theme }) => theme.colors.greyContour} 1.6rem
    ),
    repeating-linear-gradient(
      90deg,
      ${({ theme }) => theme.colors.greyContour},
      ${({ theme }) => theme.colors.greyContour} 1rem,
      transparent 1rem,
      transparent 1.6rem,
      ${({ theme }) => theme.colors.greyContour} 1.6rem
    ),
    repeating-linear-gradient(
      180deg,
      ${({ theme }) => theme.colors.greyContour},
      ${({ theme }) => theme.colors.greyContour} 1rem,
      transparent 1rem,
      transparent 1.6rem,
      ${({ theme }) => theme.colors.greyContour} 1.6rem
    ),
    repeating-linear-gradient(
      270deg,
      ${({ theme }) => theme.colors.greyContour},
      ${({ theme }) => theme.colors.greyContour} 1rem,
      transparent 1rem,
      transparent 1.6rem,
      ${({ theme }) => theme.colors.greyContour} 1.6rem
    );
  background-size: 0, 0, 2px 100%, 100% 2px;
  background-position: 0 0, 0 0, 100% 0, 0 100%;
  background-repeat: no-repeat;
`

export const Separator = styled.div<{ $desktop?: boolean; $mobile?: boolean }>`
  ${SeparatorStyleCommon};
  height: 4.9rem;
  width: 2px;

  ${({ $desktop, theme }) =>
    $desktop &&
    css`
      @media ${mq(null, sizes.tablet)} {
        display: none;
      }

      ${theme.breakpoints.up('tablet')} {
        top: 50%;
        transform: translateY(-50%);
      }
    `}

  ${({ $mobile }) =>
    $mobile &&
    css`
      width: 100%;
      top: unset;
      bottom: 0;
      @media ${mq(sizes.tablet)} {
        display: none;
      }
    `}

  @media ${mq(null, sizes.tablet)} {
    order: 3;
    height: 2px;
    width: 100%;
  }
`

export const Column = styled.div<{
  $shrink?: boolean
  $withoutBackground?: boolean
  $withPadding?: boolean
  $withoutPadding?: boolean
}>`
  width: 100%;
  padding: 2rem 0;
  display: flex;
  align-items: center;
  position: relative;

  ${({ $withoutPadding }) =>
    $withoutPadding &&
    css`
      padding: 0;
    `};

  ${({ theme }) => theme.breakpoints.up('tablet')} {
    display: table-cell;
    vertical-align: middle;

    ${({ $shrink }) =>
      $shrink &&
      css`
        width: fit-content;
        white-space: nowrap;
        flex-grow: 0;
      `};

    ${({ $withoutBackground, theme }) =>
      $withoutBackground &&
      css`
        background-color: ${theme.colors.pureWhite};
      `};

    ${({ $withPadding }) =>
      $withPadding &&
      css`
        padding: 1.1rem;
      `};
  }

  ${({ theme }) => theme.breakpoints.down('tablet')} {
    padding: 1.3rem 0;

    &:nth-child(2) {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
    }
  }
`

export const Label = styled.span`
  ${({ theme }) => theme.textStyles.firaSansTextBase};
  color: ${({ theme }) => theme.colors.blueSecondaryBis};
  flex: 1;
  padding-right: 2rem;

  ${({ theme }) => theme.breakpoints.down('tablet')} {
    display: none;
  }
`

export const HiddenLabel = styled.span`
  ${({ theme }) => theme.textStyles.firaSansTextBase};
  color: ${({ theme }) => theme.colors.blueSecondaryBis};
  flex: 1;
`

export const Line = styled.span`
  display: block;
  width: 2.5rem;
  height: 2px;
  background-color: ${({ theme }) => theme.colors.bluePrimary};
`

export const Value = styled.span<{ $firstRow?: boolean; $actions?: boolean }>`
  ${({ theme }) => theme.textStyles.montserratTextBaseMedium};

  ${({ onClick }) =>
    onClick &&
    css`
      cursor: pointer;
      text-decoration: underline;
    `}

  ${({ $firstRow }) =>
    $firstRow
      ? css`
          display: inline-block;
          min-width: 28rem;
        `
      : css`
          white-space: nowrap;
          display: flex;
        `};

  ${({ theme }) => theme.breakpoints.down('mobile')} {
    gap: 2rem;
    flex-wrap: wrap;
    justify-content: center;
  }
  ${({ theme }) => theme.breakpoints.up('tablet')} {
    padding: 0 2rem;
    justify-content: space-between;
  }

  ${({ $actions, theme }) =>
    $actions &&
    css`
      ${theme.breakpoints.up('tablet')} {
        justify-content: flex-end;
      }
    `}
`

export const Button = styled(ActionButton)<{ $isActive?: boolean }>`
  ${({ $isActive }) =>
    $isActive &&
    css`
      border-color: ${({ theme }) => theme.colors.pinkButton};
      background-color: ${({ theme }) => theme.colors.pinkButton};
      &:hover:not(:disabled) {
        border-color: ${({ theme }) => theme.colors.pinkPrimary};
        background-color: ${({ theme }) => theme.colors.pinkPrimary};
      }
    `}
  ${({ theme }) => theme.breakpoints.up('mobile')} {
    &:nth-child(n + 2) {
      margin-left: 2rem;
    }
  }
`
